// deps
import React, { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

// internal-deps
import { isEmpty } from "@swiggy-private/js-utils";
import { ConfirmOrderContext, getBridgeSdk, GlobalHeaderState, nrErrorLog } from "@swiggy-private/checkout-core";
import { GenericError, isAppPlatform, useEffectOnce, isCurrentSystemThemeDark } from "@swiggy-private/core-ui";
import { ApiResponse, OrderApiData } from "@swiggy-private/payment-types";
import { Loader } from "@swiggy-private/dls-web-core/dist/components/Loader";
import { HTTP_API_SUCCESS_STATUS_CODE } from "@swiggy-private/core-utils";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";

// helpers
import {
    checkIsTableReservation,
    getMetaDataFromOrderResponse,
    isStealStoreFlow,
} from "@dineout-payment-success-ui/helpers/index";

// types
import { MerchCarouselProps, SwgyHeaderKeysList } from "@swiggy-private/checkout-types";
import { DoSuccessPageProps } from "@dineout-payment-success-ui/interfaces";

// utils
import { DOWNSTREAM_HOSTS } from "@dineout-payment-success-ui/utils/index";
import { getSubPage, getSuccessPagePresentationalMeta } from "@dineout-payment-success-ui/helpers/TableReservation";
import useCelebrationAssetsPrefetch from "@dineout-payment-success-ui/hooks/useCelebrationAssetsPrefetch";

// components
import SuccessPage from "./SuccessPage";
import TableReservationSuccessPage from "./TableReservationSuccessPage";
import StealDealSuccessPage from "./StealDealSuccessPage";

// constants
import {
    DealCategory,
    FETCHING_BOOKING_DETAILS,
    STATUS_BAR_COLOR_DARK,
    STATUS_BAR_COLOR_DEFAULT,
} from "@dineout-payment-success-ui/constants";

// apis
import {
    getBookingDetailsResponse,
    getMerchBanners,
    getOrderInfo,
} from "@dineout-payment-success-ui/apis/BookingSuccess";
import { LoadingContainer } from "./styles";

const DineoutPaymentSuccess: React.FC<DoSuccessPageProps> = (props) => {
    const { confirmOrderContext } = useContext(ConfirmOrderContext);
    const [orderRawData, setOrderData] = useState<ApiResponse<OrderApiData> | undefined | null>(confirmOrderContext);
    const [carouselData, setCarouselData] = useState({} as MerchCarouselProps);
    const [plPageResponse, setPlPageResponse] = useState({});
    const [loading, setLoading] = useState<boolean>(false);
    const metaDataJson = getMetaDataFromOrderResponse(orderRawData);
    const orderDetailsApiCallRef = useRef<boolean>(false);
    const bookingDetailsApiCallRef = useRef<boolean>(false);
    const [error, setError] = useState<boolean>(false);
    const nativeBridgeCalledRef = useRef<boolean>(false);

    const fetchOrderData = useCallback(
        async (orderId?: string, _orderRawData?: ApiResponse<OrderApiData>) => {
            if (isEmpty(orderId)) {
                setLoading(false);
                setError(true);
                return;
            }

            setLoading(true);
            if (isEmpty(_orderRawData) && !orderDetailsApiCallRef.current) {
                orderDetailsApiCallRef.current = true;
                const orderInfo = await getOrderInfo(orderId);
                if (
                    orderInfo !== null &&
                    "statusCode" in orderInfo &&
                    orderInfo.statusCode === HTTP_API_SUCCESS_STATUS_CODE
                ) {
                    const dashOrderResponse = orderInfo.response as ApiResponse<OrderApiData>;
                    setOrderData(dashOrderResponse);
                }
                orderDetailsApiCallRef.current = false;
            }

            if (!isEmpty(_orderRawData) && isEmpty(plPageResponse) && !bookingDetailsApiCallRef.current) {
                bookingDetailsApiCallRef.current = true;
                const metaDataJson = getMetaDataFromOrderResponse(_orderRawData);
                const subPage = getSubPage(metaDataJson?.dealCategory || "");

                const bookingDetails = await getBookingDetailsResponse(orderId, subPage);
                if (!isEmpty(bookingDetails) && !isEmpty(bookingDetails.success)) {
                    setPlPageResponse(bookingDetails);
                }
                bookingDetailsApiCallRef.current = false;
            }
            setLoading(false);
        },
        [plPageResponse],
    );

    const postOrderState = !!confirmOrderContext;
    const isTableReservation = useMemo(
        () => checkIsTableReservation(metaDataJson?.dealCategory, metaDataJson?.type),
        [metaDataJson?.dealCategory, metaDataJson?.type],
    );

    const isEventReservation = useMemo(
        () => metaDataJson?.dealCategory === DealCategory.EVENTS,
        [metaDataJson?.dealCategory],
    );

    const isStealStoreOrder = useMemo(() => isStealStoreFlow(metaDataJson?.dealCategory), [metaDataJson?.dealCategory]);

    useCelebrationAssetsPrefetch(isTableReservation);

    useEffectOnce(() => {
        const _isCurrentSystemThemeDark = isCurrentSystemThemeDark();
        if (isEventReservation) {
            getBridgeSdk()
                .changeStatusBarColor({
                    style: _isCurrentSystemThemeDark ? STATUS_BAR_COLOR_DARK : STATUS_BAR_COLOR_DEFAULT,
                    statusBarMode: _isCurrentSystemThemeDark ? "lightContent" : "default",
                    bottomBarMode: _isCurrentSystemThemeDark ? "lightContent" : "default",
                })
                .catch(() => void 0);
        }
    });

    useEffect(() => {
        const fetchData = async () => {
            const platform = GlobalHeaderState.getHeader(SwgyHeaderKeysList.platform);
            const startTime = Date.now();
            try {
                // Show merch banners only for regular dineout orders on app platform
                if (!isTableReservation && isAppPlatform(platform)) {
                    const carouselResponse = await getMerchBanners();
                    if (carouselResponse) {
                        setCarouselData(carouselResponse);
                    }
                }
            } catch (error) {
                nrErrorLog({ url: DOWNSTREAM_HOSTS.DISC_SWIGGY, startTime }, error, "");
            }

            try {
                if (props?.orderId && isEmpty(orderRawData)) {
                    await fetchOrderData(props.orderId);
                } else if (!isEmpty(orderRawData) && isEmpty(plPageResponse) && isTableReservation) {
                    const orderId = props.orderId || orderRawData?.data?.orders?.[0]?.orderId;
                    await fetchOrderData(orderId, orderRawData);
                }
            } catch (error) {
                setLoading(false);
                setError(true);
                nrErrorLog({ url: DOWNSTREAM_HOSTS.DO_HOST, startTime }, error, "");
            }
        };

        fetchData();
    }, [
        metaDataJson?.dealCategory,
        metaDataJson?.type,
        props.orderId,
        isTableReservation,
        orderRawData,
        fetchOrderData,
        plPageResponse,
    ]);

    const reloadSuccessPage = useCallback(() => {
        const currentOrderId = orderRawData?.data?.orders?.[0]?.orderId;
        setOrderData(null);
        setPlPageResponse({});
        fetchOrderData(currentOrderId);
        nativeBridgeCalledRef.current = false;
    }, [fetchOrderData, orderRawData]);

    const renderLoadingState = useCallback(
        () => (
            <LoadingContainer>
                <Loader
                    size="large"
                    type="secondary"
                    foregroundColor="positive"
                    backgroundColor="positive_Sub"
                    isDisabled={false}
                />
                <Text variant="V2.body1.neutral.bold" color="text_Med_Emphasis">
                    {FETCHING_BOOKING_DETAILS}
                </Text>
            </LoadingContainer>
        ),
        [],
    );

    if (loading || !orderRawData) return renderLoadingState();

    if (error) {
        return <GenericError />;
    }

    if (isStealStoreOrder) {
        return <StealDealSuccessPage {...orderRawData} reloadSuccessPage={reloadSuccessPage} />;
    }

    if (isEventReservation || isTableReservation) {
        const presentationalMeta = getSuccessPagePresentationalMeta(
            orderRawData,
            isEventReservation,
            isTableReservation ? plPageResponse : undefined,
        );
        return (
            <TableReservationSuccessPage
                {...orderRawData}
                presentationalMeta={presentationalMeta}
                isEventReservation={isEventReservation}
                reloadSuccessPage={reloadSuccessPage}
                nativeBridgeCalledRef={nativeBridgeCalledRef}
            />
        );
    }

    return <SuccessPage {...orderRawData} postOrderState={postOrderState} carouselData={carouselData} />;
};

export default memo(DineoutPaymentSuccess);
